// Read-only "where the prompts live in the repo" reference. Carried over
// from the original `AiPromptingPanel` as a separate section so the
// editable card stack at the top of the panel doesn't compete with it for
// visual real estate. This is purely an inventory; the data lives in
// `docs/ai_prompts_inventory.md` and the catalog is the source of truth
// for which prompts are actually editable.

import { useEffect, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";

export type AppMode =
  | "leads"
  | "salesConsole"
  | "training"
  | "knowledgebase"
  | "aiPrompting"
  | "adsAssistant"
  | "locationTools"
  | "operations"
  | "dealTypes"
  | "dashboard"
  | "admin"
  | "dev"
  | "platform";

type PromptSurface = {
  mode: AppMode;
  area: string;
  file: string;
  notes: string;
};

const MODE_LABELS: Array<{ id: AppMode; label: string }> = [
  { id: "leads", label: "Leads" },
  { id: "salesConsole", label: "Sales Console" },
  { id: "training", label: "Training" },
  { id: "knowledgebase", label: "Knowledge Base" },
  { id: "adsAssistant", label: "Ads Assistant" },
  { id: "locationTools", label: "Location Tools" },
  { id: "operations", label: "Deals" },
  { id: "dealTypes", label: "Deal Types" },
  { id: "dashboard", label: "Dashboard" },
  { id: "admin", label: "Admin" },
  { id: "dev", label: "Dev" },
  { id: "platform", label: "Platform" },
  { id: "aiPrompting", label: "AI Prompting" },
];

const PROMPT_SURFACES: PromptSurface[] = [
  {
    mode: "knowledgebase",
    area: "KB split proposals",
    file: "server/kb-admin-handlers/splitProposal.ts",
    notes: "Split system prompt plus user-message template for article split suggestions.",
  },
  {
    mode: "knowledgebase",
    area: "KB assist / article prep",
    file: "server/kb-admin-handlers/assist.ts",
    notes: "Primary KB assistant prompt assembly for drafting and metadata prep.",
  },
  {
    mode: "knowledgebase",
    area: "KB gap solution suggestions",
    file: "server/kb-admin-handlers/suggestGapSolution.ts",
    notes: "Prompting for suggested fixes tied to gap inbox items.",
  },
  {
    mode: "knowledgebase",
    area: "KB general-info proposals",
    file: "server/kb-admin-handlers/submitGeneralInfoProposal.ts",
    notes: "Prompting used to summarize and shape general info proposals.",
  },
  {
    mode: "knowledgebase",
    area: "KB answer execution",
    file: "api/kb/answerExecution.ts",
    notes: "Runtime answer path instructions and model behavior for KB answers.",
  },
  {
    mode: "leads",
    area: "Lead extraction",
    file: "api/lead/extract.ts",
    notes: "Structured extraction prompt flow from lead transcript content. Editable via the catalog above (`lead_extract_v2`).",
  },
  {
    mode: "training",
    area: "Training roleplay",
    file: "server/admin-handlers/trainingRoleplay.ts",
    notes: "Customer simulation prompting used in roleplay generation.",
  },
  {
    mode: "training",
    area: "Training roleplay library",
    file: "server/admin-handlers/trainingRoleplayLibrary.ts",
    notes: "Prompting that supports roleplay scenario generation and packaging.",
  },
  {
    mode: "adsAssistant",
    area: "Ads library assist",
    file: "server/admin-handlers/adsLibraryAssist.ts",
    notes: "Ad guidance helper prompt surfaces for assistant-style recommendations.",
  },
  {
    mode: "adsAssistant",
    area: "Social post analysis",
    file: "server/admin-handlers/socialPostStubAnalysisPersist.ts",
    notes: "Prompting used to analyze social post drafts and persist findings.",
  },
  {
    mode: "knowledgebase",
    area: "KB embeddings ingest",
    file: "scripts/ingestKb.ts",
    notes: "Embedding path and text prep comments for KB ingest pipeline.",
  },
  {
    mode: "aiPrompting",
    area: "Prompt inventory reference",
    file: "docs/ai_prompts_inventory.md",
    notes: "Maintenance map of prompt locations until centralization work lands.",
  },
];

export function PromptSurfacesReference() {
  const [selectedMode, setSelectedMode] = useState<AppMode>(() => {
    if (typeof window === "undefined") return "knowledgebase";
    const raw = window.localStorage.getItem("aiPrompting:selectedMode");
    const valid = MODE_LABELS.some((m) => m.id === raw);
    return valid ? (raw as AppMode) : "knowledgebase";
  });
  const visibleRows = PROMPT_SURFACES.filter((row) => row.mode === selectedMode);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("aiPrompting:selectedMode", selectedMode);
  }, [selectedMode]);

  // Container/padding doctrine \u00a7K4: drop the outer `rounded-2xl` violet
  // section in favor of <SectionCard>. The table inside is the single inner
  // rounded shell. Previously stacked rounded-2xl > rounded-2xl > rounded-xl
  // (the "huge stacked containers" user complaint).
  return (
    <SectionCard
      title="Prompt surfaces reference"
      description="Where each AI prompt lives in the repo. Until each is registered in the catalog above, edits to those files require a code change."
    >
      {/* Horizontal scroll instead of flex-wrap so the rounded-full pill
          shape stays correct on narrow viewports. Previously this used
          `flex-wrap`, which on a 360px screen wrapped the 12 mode labels to
          ~4 rows, but `rounded-full` (border-radius: 9999px) on a now-tall
          element rendered as a giant elongated capsule \u2014 the user reported
          it as "a huge transparent pill button that got extended really
          big." Dev sync note d9508764. `flex-nowrap overflow-x-auto` keeps
          a single-row stadium with native touch-scrolling, and
          `whitespace-nowrap` ensures multi-word labels ("Sales Console",
          "Knowledge Base") never break inside their button. */}
      <div className="flex flex-nowrap items-center gap-1 overflow-x-auto rounded-full border border-gray-200 bg-gray-50 p-1 dark:border-zinc-700 dark:bg-zinc-900/40">
        {MODE_LABELS.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setSelectedMode(m.id)}
            className={
              "shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-xs sm:text-sm " +
              (selectedMode === m.id
                ? "bg-white text-violet-800 shadow-sm dark:bg-zinc-800 dark:text-violet-200"
                : "text-gray-600 hover:bg-white/60 dark:text-zinc-300 dark:hover:bg-zinc-800/60")
            }
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900/50">
        <div className="grid grid-cols-12 gap-2 border-b border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:border-zinc-700 dark:text-zinc-300">
          <div className="col-span-4">Area</div>
          <div className="col-span-4">File</div>
          <div className="col-span-4">Notes</div>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-zinc-800">
          {visibleRows.map((row) => (
            <div key={`${row.area}:${row.file}`} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm">
              <div className="col-span-4 font-medium text-gray-900 dark:text-zinc-100">{row.area}</div>
              <div className="col-span-4 break-all font-mono text-xs text-violet-700 dark:text-violet-300">{row.file}</div>
              <div className="col-span-4 text-gray-700 dark:text-zinc-300">{row.notes}</div>
            </div>
          ))}
          {visibleRows.length === 0 ? (
            <div className="px-4 py-4 text-sm text-gray-600 dark:text-zinc-300">
              No prompt surfaces are mapped to this mode yet.
            </div>
          ) : null}
        </div>
      </div>
    </SectionCard>
  );
}
